import React, { useCallback } from 'react'
import {
    Form,
    type FormikBag,
    type FormikProps,
    withFormik,
} from 'formik'
import { Dialog, DialogContent, Grid } from '@mui/material'
import { array, object, string } from 'yup'
import { PrimaryText } from '../../../ui/Text'
import { BLACK } from '../../../ui/Colors'
import { DialogFooter } from '../../Dialog/BasicFooterDialog'
import { CheckboxList } from '../../../ui/CheckboxList'
import { DialogHeader } from '../../Dialog/BasicDialog'
import { EnhancedField, TextInput } from '../../../ui/Inputs'
import { RadioButtonsGroup } from '../../../ui/RadioButtonsGroup'
import { Article, ArticleSection } from '../../../../redux/supportAndGuides/supportArticlesType'
import { ImageUploader } from '../../PhotoUploader'
import { useAppDispatch } from '../../../../redux/hooks'
import { updateArticle } from '../../../../redux/supportAndGuides/supportArticlesSlice'
import { BUListToDisplay, inputsArticleForm, sections } from './utils'

interface FormValues {
    _id: string
    title: string
    description: string
    BUList?: string[]
    link: string
    section: ArticleSection
    image: string
}

interface FormProps {
    isDialogOpen: boolean
    setIsDialogOpen: any
    initialValues: FormValues
    onSubmit: (values: FormValues) => void
    articleValues: Article
}

const PureSettingsForm = ({
    isDialogOpen,
    setIsDialogOpen,
    articleValues,
    ...props
}: FormikProps<FormValues> & FormProps): JSX.Element => {

    const getUploadName = (fileName: string) => {
        props.setFieldValue('image', fileName)
    }

    const handleChangeJobs = (BUSelected: string) => {
        const isInclude = props.values.BUList.includes(BUSelected)

        if (isInclude) {
            props.setFieldValue(
                'BUList',
                props.values.BUList.filter((el) => el !== BUSelected)
            )
        } else {
            props.setFieldValue('BUList', [...props.values.BUList, BUSelected])
        }
    }
    return (
        <Dialog
            open={isDialogOpen}
            fullWidth={true}
            onClose={() => setIsDialogOpen(false)}
            PaperProps={{
                sx: {
                    borderRadius: '1em',
                    minWidth: '55vw',
                    minHeight: '70vh',
                    padding: '2vh 2vw 2vh 2vw',
                },
            }}
        >
            <DialogHeader
                title={"Modifier l'article"}
                setOpen={() => setIsDialogOpen(false)}
            />
            <Form style={{ width: '100%' }}>
                <DialogContent sx={{ height: '65vh' }}>
                    <Form>
                        <Grid container direction="column">
                            {inputsArticleForm.map((input, index) => (
                                <EnhancedField
                                    key={index}
                                    type={input.type}
                                    name={input.name}
                                    label={input.label}
                                    autoFocus={index === 0}
                                    margin="normal"
                                    component={TextInput}
                                />
                            ))}
                            <Grid container justifyContent={"space-between"}>
                                <Grid width="40%">
                                    <CheckboxList titleList="BU à qui mettre cet article" list={BUListToDisplay} handleChange={handleChangeJobs} checkedList={props.values.BUList} />
                                </Grid>
                                <Grid mt="2vh" width="40%">
                                    <RadioButtonsGroup
                                        onChange={(event, value) =>
                                            props.setFieldValue('section', value)
                                        }
                                        values={sections}
                                        label={"Rubrique de l'article"}
                                        defaultCheck={ArticleSection.FIRST_STEPS}
                                    />
                                </Grid>
                            </Grid>
                            <Grid mt="3vh">
                                <PrimaryText bold color={BLACK} mb="1vh">Ajouter une nouvelle image</PrimaryText>
                                <ImageUploader getUploadName={getUploadName} />
                            </Grid>
                        </Grid>
                    </Form>
                </DialogContent>
                <DialogFooter
                    leftButtonFunc={() => {
                        setIsDialogOpen(false)
                    }
                    }
                    leftButtonText={'FERMER'}
                    rightButtonFunc={async () => {
                        await props.submitForm()
                    }}
                    rightButtonText={
                        `MODIFIER`
                    }
                />
            </Form>
        </Dialog>
    )
}

const ControlledSettingsForm = withFormik<FormProps, FormValues>({
    mapPropsToValues: (props: FormProps) => ({
        _id: props.initialValues._id,
        title: props.initialValues.title,
        description: props.initialValues.description,
        BUList: props.initialValues.BUList,
        link: props.initialValues.link,
        section: props.initialValues.section,
        image: props.initialValues.image,
    }),
    validationSchema: () => {
        return object().shape({
            BUList: array()
                .of(string())
                .min(1, `Merci de cocher au moins une BU`),
            title: string().required('Le champs Titre est obligatoire'),
            description: string().required('Le champs description est obligatoire'),
            link: string().required('Le champs link est obligatoire'),
            section: string().required('Le champs Rubrique est obligatoire'),
            image: string().required('Le champs Image est obligatoire'),

        })
    },
    handleSubmit: (
        values: FormValues,
        bag: FormikBag<FormProps, FormValues>
    ) => {
        bag.props.onSubmit(values)
    },
})(PureSettingsForm)

const ModificationDialog = ({ isDialogOpen, setIsDialogOpen, values, closeMenu }: { isDialogOpen: boolean, setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>, values: Article, closeMenu: React.Dispatch<React.SetStateAction<boolean>> }) => {
    const dispatch = useAppDispatch()
    const _updateArticle = useCallback(
        async (formData: FormValues) => {
            await dispatch(updateArticle(formData))
        },
        [dispatch]
    )

    const handleSubmit = (formValues: FormValues) => {
        setIsDialogOpen(false)
        closeMenu(false)
        _updateArticle(formValues)
    }
    return (
        <ControlledSettingsForm
            setIsDialogOpen={setIsDialogOpen}
            onSubmit={handleSubmit}
            initialValues={values}
            isDialogOpen={isDialogOpen}
            articleValues={values}
        />
    )
}

export default ModificationDialog
